import api from './api';

/**
 * Support Desk API (/admin/support/*). METADATA ONLY.
 *
 * Interface names and field names mirror backend/app/schemas/support.py exactly;
 * backend/tests/test_support_contract_parity.py fails if they drift. There is
 * deliberately no field for a matter title, party name, case number, FIR or
 * document text.
 */

export type ReasonCodeLit =
  | 'broke'
  | 'hearing_unprepared'
  | 'stuck'
  | 'gone_quiet'
  | 'trial_ending'
  | 'doing_great';
export type TabLit = 'call_today' | 'broke' | 'trial_ending' | 'doing_great' | 'later';
export type ChannelLit = 'call' | 'whatsapp' | 'email';
export type OutcomeLit = 'talked_ok' | 'needs_help' | 'no_answer' | 'call_back' | 'spoke_to_junior';

export interface SupportStatus {
  enabled: boolean;
}

export interface LastContactOut {
  staff_email: string;
  at: string;
  outcome: OutcomeLit;
}

export interface ContactHistoryOut {
  staff_email: string;
  at: string;
  outcome: OutcomeLit;
  note?: string | null;
}

export interface OpenedByOut {
  staff_email: string;
  minutes_ago: number;
}

export interface SupportCardOut {
  user_id: string;
  display_name: string;
  vertical: string;
  tier_label: string;
  reason_code: ReasonCodeLit;
  reason_text: string;
  sublabel?: string | null;
  meta_line: string;
  notes: string[];
  last_seen_at?: string | null;
  last_contact?: LastContactOut | null;
  has_phone: boolean;
  phone_masked?: string | null;
  in_court_today?: boolean; // has a hearing today: likely in court, call after 4 pm IST
  phone_verified?: boolean; // has_phone with an unverified number is still callable
  opened_by?: OpenedByOut | null; // another staff member opened this advocate in the last 30 min
}

export interface SupportQueueResponse {
  tab: TabLit;
  tabs: Record<string, number>;
  cards: SupportCardOut[];
}

export interface JourneyOut {
  signed_up: boolean;
  added_case: boolean;
  checked_fir?: boolean | null; // null = not a step in this advocate's practice
  prepared_hearing?: boolean | null;
}

export interface ActivityDayOut {
  day: string;
  requests: number;
}

export interface ErrorSummaryOut {
  code: string;
  source: string;
  count: number;
  last_seen: string;
}

export interface SupportUserPanel {
  user_id: string;
  display_name: string;
  vertical: string;
  email?: string | null;
  phone?: string | null;
  has_phone: boolean;
  phone_verified?: boolean;
  best_number?: string | null; // staff-written, from the latest contact that gave it
  best_time?: string | null;
  tier_label: string;
  last_seen_at?: string | null;
  reason_code?: ReasonCodeLit | null;
  reason_text?: string | null;
  headline: string;
  notes?: string[];
  journey: JourneyOut;
  next_hearing_date?: string | null;
  hearing_prepared?: boolean | null;
  activity: ActivityDayOut[];
  recent_errors: ErrorSummaryOut[];
  opener: string;
  last_contact?: LastContactOut | null;
  contacts?: ContactHistoryOut[]; // newest first, up to five
  error_refs?: string[]; // request ids of their last server errors, newest first
}

export interface ContactIn {
  channel: ChannelLit;
  outcome: OutcomeLit;
  note?: string | null;
  remind_at?: string | null;
  best_number?: string | null;
  best_time?: string | null;
}

export interface ContactOut {
  id: number;
  created_at: string;
}

export interface ErrorFeedItem {
  code: string;
  source: string;
  route?: string | null;
  feature_label?: string | null; // the nav's own name for the page; null when unknown
  count: number;
  users: number;
  last_seen: string;
}

export interface ErrorFeedResponse {
  items: ErrorFeedItem[];
}

export type JobLit = 'court-watch' | 'whatsapp-tick' | 'drive-sync';
export type JobStateLit = 'ok' | 'watch' | 'down' | 'off' | 'unknown';

export interface LikelyCallerOut {
  user_id: string;
  display_name: string;
  feature_label?: string | null;
  minutes_ago: number;
}

export interface JobLineOut {
  job: JobLit;
  name: string;
  state: JobStateLit;
  headline: string;
  say_to_caller?: string | null;
  do_next?: string | null;
  last_run_at?: string | null;
  facts: Record<string, number>;
}

export interface PlatformOut {
  likely_to_call: LikelyCallerOut[];
  jobs: JobLineOut[];
}

export interface IncidentIn {
  message: string; // admin-written, for advocates: it leads the "broke" opener
}

export interface IncidentOut {
  message: string;
  set_by_email: string;
  set_at: string;
}

export const supportApi = {
  status: () => api.get<SupportStatus>('/admin/support/status').then((r) => r.data),
  queue: (tab: TabLit) =>
    api.get<SupportQueueResponse>('/admin/support/queue', { params: { tab } }).then((r) => r.data),
  panel: (userId: string) =>
    api.get<SupportUserPanel>(`/admin/support/users/${userId}`).then((r) => r.data),
  recordContact: (userId: string, body: ContactIn) =>
    api.post<ContactOut>(`/admin/support/users/${userId}/contacts`, body).then((r) => r.data),
  errors: () => api.get<ErrorFeedResponse>('/admin/support/errors').then((r) => r.data),
  incident: () => api.get<IncidentOut | null>('/admin/support/incident').then((r) => r.data),
  platform: () => api.get<PlatformOut>('/admin/support/platform').then((r) => r.data),
  setIncident: (body: IncidentIn) =>
    api.put<IncidentOut>('/admin/support/incident', body).then((r) => r.data),
  clearIncident: () => api.delete('/admin/support/incident').then(() => undefined),
};
